// Frontend/src/pages/auth/LoginPage.tsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { queryClient } from "@/lib/queryClient";
import {
  loginResidentWithPassword,
  persistResidentAuthenticatedState,
  persistResidentSelectionState,
  requestResidentLoginOtp,
} from "@/api/residentAuth";
import logoImg from "@/assets/logo.png";
import { useLanguage } from "@/contexts/LanguageContext";

const PRIMARY = "#1F3A93";
const ACCENT = "#27AE60";
const BG = "#F5F6FA";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth(); // <-- also read user for auto-redirect
  const { t } = useLanguage();

  const [userType, setUserType] = React.useState<"hoa" | "resident">("hoa");
  const [usePasswordForResident, setUsePasswordForResident] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Check for HOA/Super Admin session first (uses sessionStorage via AuthContext)
    if (user && user.role !== "RESIDENT") {
      navigate(roleHome(user.role), { replace: true });
      return;
    }

    // Check for resident session.
    // Accept both localStorage resident token and sessionStorage resident session
    // to avoid redirect loops when resident signs in from HOA tab.
    const residentToken = localStorage.getItem('resident_access_token');
    const accessToken = sessionStorage.getItem('access_token');
    const role = sessionStorage.getItem('role');
    if (residentToken || (accessToken && role === 'RESIDENT')) {
      // Resident is logged in, redirect to resident dashboard
      navigate('/resident', { replace: true });
    }
  }, [user, navigate]);

  function hexToRgba(hex: string, alpha = 1) {
    const h = hex.replace("#", "");
    const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  function roleHome(role?: string | null) {
    switch (role) {
      case "SUPER_ADMIN":
        return "/super-admin";
      case "HOA_ADMIN":
        return "/hoa-admin";
      case "RESIDENT":
      default:
        return "/resident";
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (userType === "hoa") {
        // HOA password login - uses sessionStorage + AuthContext
        const u = await login(email.trim(), password);

        // Bridge resident sessions to resident token storage format.
        // This prevents residents from being bounced back to /login
        // when they signed in from the HOA tab.
        if (u.role === 'RESIDENT') {
          const access = sessionStorage.getItem('access_token');
          if (access) {
            localStorage.setItem('resident_access_token', access);
            localStorage.setItem('resident_id', u.id);
            localStorage.setItem('resident_name', `${u.first_name} ${u.last_name}`.trim() || u.email);
            localStorage.setItem('resident_email', u.email);
          }
        }

        navigate(roleHome(u.role), { replace: true });
      } else {
        // Resident login
        if (usePasswordForResident) {
          const residentAuth = await loginResidentWithPassword({
            email: email.trim(),
            password,
          });

          queryClient.clear();

          if (residentAuth.requires_property_selection) {
            persistResidentSelectionState(residentAuth);
            navigate("/resident/select-property", { replace: true });
          } else if (residentAuth.access_token) {
            persistResidentAuthenticatedState(residentAuth);
            navigate(
              residentAuth.has_completed_setup ? "/resident" : "/resident/setup",
              { replace: true }
            );
          } else {
            throw new Error(t("auth.signInError"));
          }
        } else {
          await requestResidentLoginOtp({ email: email.trim() });
          navigate(`/resident-otp-verify?email=${encodeURIComponent(email.trim())}&mode=login`);
        }
      }
    } catch (err: any) {
      
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.message ||
        t("auth.signInError");
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BG }}>
      <div className="w-full max-w-[440px] md:rounded-3xl md:border md:border-black/10 md:bg-white md:shadow-md p-6 md:p-10">
        {/* Logo & Title */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={logoImg}
            alt="Urbe360 logo"
            width={124}
            height={124}
            className="mb-2"
          />
          <span className="text-[18px] font-semibold text-gray-900">Urbe360</span>
        </div>

        <h1 className="text-center text-3xl font-semibold mb-2 text-gray-900">
          {t("auth.signIn")}
        </h1>
        <p className="text-center text-sm text-gray-600 mb-6">
          {userType === "hoa" 
            ? t("auth.hoaLoginHint")
            : usePasswordForResident
            ? t("auth.residentPasswordHint")
            : t("auth.residentOtpHint")}
        </p>

        {/* User Type Tabs */}
        <div className="flex gap-2 mb-6 p-1 rounded-2xl bg-gray-100">
          <button
            type="button"
            onClick={() => setUserType("hoa")}
            className={`flex-1 rounded-xl py-2.5 text-sm font-medium transition-all ${
              userType === "hoa"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {t("roles.hoaAdminFull")}
          </button>
          <button
            type="button"
            onClick={() => setUserType("resident")}
            className={`flex-1 rounded-xl py-2.5 text-sm font-medium transition-all ${
              userType === "resident"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {t("roles.resident")}
          </button>
        </div>

        {/* Resident Login Toggle */}
        {userType === "resident" && (
          <div className="mb-6 flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200">
            <span className="text-sm text-gray-700">{t("auth.loginWithPassword")}</span>
            <button
              type="button"
              onClick={() => setUsePasswordForResident(!usePasswordForResident)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                usePasswordForResident ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  usePasswordForResident ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm text-gray-800">
              {t("auth.emailOrId")}
            </label>
            <input
              id="email"
              type="text"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder={t("auth.emailPlaceholder")}
              className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 outline-none transition"
              style={{ borderColor: "rgba(0,0,0,0.12)" }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = PRIMARY;
                e.currentTarget.style.boxShadow = `0 0 0 4px ${hexToRgba(ACCENT, 0.14)}`;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
                e.currentTarget.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Password */}
          {(userType === "hoa" || (userType === "resident" && usePasswordForResident)) && (
            <div className="space-y-2">
            <label htmlFor="password" className="block text-sm text-gray-800">
              {t("auth.password")}
            </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  placeholder="••••••••"
                  className="w-full rounded-2xl bg-white text-gray-900 placeholder:text-gray-400 border px-4 py-3 pr-12 outline-none transition"
                  style={{ borderColor: "rgba(0,0,0,0.12)" }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = PRIMARY;
                    e.currentTarget.style.boxShadow = `0 0 0 4px ${hexToRgba(ACCENT, 0.14)}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute inset-y-0 right-0 pr-3 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          {(userType === "hoa" || (userType === "resident" && usePasswordForResident)) && (
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-black/20 focus:ring-2"
                  style={{ accentColor: ACCENT }}
                  disabled={isLoading}
                />
                {t("auth.keepSignedIn")}
              </label>
              <Link to="/forgot-password" className="text-sm underline underline-offset-4" style={{ color: PRIMARY }}>
                {t("auth.forgotPassword")}
              </Link>
            </div>
          )}

          {userType === "resident" && !usePasswordForResident && (
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t("auth.otpInfo")}
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="group inline-flex w-full items-center justify-center rounded-2xl px-4 py-3 text-[15px] font-semibold text-white transition-all active:scale-[0.99] disabled:opacity-60"
            style={{ background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY} 70%, ${ACCENT} 100%)` }}
          >
            {isLoading ? (
              (userType === "hoa" || usePasswordForResident)
                ? t("auth.signingIn")
                : t("auth.redirecting")
            ) : (
              (userType === "hoa" || usePasswordForResident)
                ? t("auth.continue")
                : t("auth.continueWithOtp")
            )}
            <ArrowRightCircleIcon className="ml-2 h-5 w-5 transition group-hover:translate-x-0.5" />
          </button>
        </form>
      </div>
    </div>
  );
}

/* Inline icons to avoid extra deps */
function EyeIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function EyeSlashIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 3l18 18M2 12s3.5-7 10-7c2.4 0 4.4.8 6 2M22 12s-3.5 7-10 7c-2.4 0-4.4-.8-6-2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRightCircleIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" opacity="0.85" />
      <path d="M10 8l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
